import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "./ui/popover";
import { Bell, Check, CheckCheck, Inbox } from "lucide-react";
import { fetchMyNotifications, markNotificationsRead } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const POLL_INTERVAL_MS = 30000;

const KIND_DOT = {
    rental_requested: "bg-[#737a3d]",
    rental_accepted: "bg-[#737a3d]",
    rental_declined: "bg-[#B56C5A]",
};

const formatRelative = (iso) => {
    if (!iso) return "";
    try {
        const then = new Date(iso).getTime();
        const diff = Date.now() - then;
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        return `${days}d ago`;
    } catch {
        return "";
    }
};

export const NotificationBell = () => {
    const { isAuthenticated } = useAuth();
    const [open, setOpen] = useState(false);
    const [list, setList] = useState([]);
    const [unread, setUnread] = useState(0);
    const timerRef = useRef(null);

    const load = async () => {
        try {
            const data = await fetchMyNotifications();
            setList(data.notifications || []);
            setUnread(data.unread || 0);
        } catch {
            /* silent — endpoint requires auth */
        }
    };

    useEffect(() => {
        if (!isAuthenticated) {
            setList([]);
            setUnread(0);
            return undefined;
        }
        load();
        timerRef.current = setInterval(load, POLL_INTERVAL_MS);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [isAuthenticated]);

    const handleOpenChange = async (next) => {
        setOpen(next);
        if (next && unread > 0) {
            // Optimistically mark them read
            setUnread(0);
            setList((rs) => rs.map((r) => ({ ...r, read: true })));
            try {
                await markNotificationsRead(null);
            } catch {
                /* ignore */
            }
        }
    };

    const handleMarkAllRead = async () => {
        // Explicit button — also works when auto-mark-on-open didn't run
        // (e.g. the popover was already open when a new notification arrived).
        if (unread === 0) return;
        setUnread(0);
        setList((rs) => rs.map((r) => ({ ...r, read: true })));
        try {
            await markNotificationsRead(null);
        } catch {
            /* ignore */
        }
    };

    if (!isAuthenticated) return null;

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    aria-label="Notifications"
                    data-testid="notification-bell"
                    className="relative inline-flex h-9 w-9 items-center justify-center rounded-full text-[#2e3220] transition-colors hover:bg-[#2e3220]/5 focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                >
                    <Bell className="h-4 w-4" strokeWidth={2.25} />
                    {unread > 0 && (
                        <span
                            data-testid="notification-unread-count"
                            className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#B56C5A] px-1 text-[10px] font-bold text-[#F4F1EA]"
                        >
                            {unread > 9 ? "9+" : unread}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                sideOffset={8}
                data-testid="notification-popover"
                className="w-[360px] max-w-[90vw] rounded-2xl border border-[#2e3220]/10 bg-[#F4F1EA] p-0 shadow-[0_18px_50px_rgba(46,50,32,0.18)]"
            >
                <div className="flex items-start justify-between gap-3 border-b border-[#2e3220]/10 p-4">
                    <div className="min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                            Notifications
                        </p>
                        <p className="mt-0.5 font-display text-base font-bold text-[#2e3220]">
                            {unread > 0 ? `${unread} new` : "You're all caught up"}
                        </p>
                    </div>
                    {list.length > 0 && (
                        <button
                            type="button"
                            data-testid="notification-mark-all-read"
                            onClick={handleMarkAllRead}
                            disabled={unread === 0}
                            className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-[#737a3d]/40 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#737a3d] transition-colors hover:bg-[#737a3d]/10 disabled:cursor-default disabled:opacity-40"
                        >
                            <CheckCheck className="h-3 w-3" strokeWidth={2.5} />
                            Mark all read
                        </button>
                    )}
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                    {list.length === 0 ? (
                        <div className="flex flex-col items-center p-8 text-center">
                            <Inbox className="mb-2 h-7 w-7 text-[#737a3d]/60" />
                            <p className="text-sm text-[#5a6142]">
                                Nothing yet. Rental activity will show up here.
                            </p>
                        </div>
                    ) : (
                        <ul className="divide-y divide-[#2e3220]/10">
                            {list.map((n) => (
                                <li
                                    key={n.id}
                                    data-testid={`notification-${n.id}`}
                                    className={!n.read ? "bg-[#737a3d]/5" : ""}
                                >
                                    <Link
                                        to={n.link || "/"}
                                        onClick={() => setOpen(false)}
                                        className="flex items-start gap-3 p-4 transition-colors hover:bg-[#737a3d]/10"
                                    >
                                        <span
                                            className={`mt-1.5 inline-block h-2 w-2 flex-shrink-0 rounded-full ${KIND_DOT[n.kind] || "bg-[#737a3d]"}`}
                                        />
                                        <div className="min-w-0 flex-1">
                                            <p className="font-display text-sm font-bold leading-tight text-[#2e3220]">
                                                {n.title}
                                            </p>
                                            {n.body && (
                                                <p className="mt-0.5 text-xs leading-relaxed text-[#5a6142]">
                                                    {n.body}
                                                </p>
                                            )}
                                            <p className="mt-1 text-[10px] uppercase tracking-widest text-[#5a6142]/70">
                                                {formatRelative(n.created_at)}
                                            </p>
                                        </div>
                                        {n.read && (
                                            <Check className="mt-1 h-3 w-3 text-[#737a3d]/40" />
                                        )}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default NotificationBell;
